import { useCallback, useEffect, useRef, useState } from "react";

import { useProjectStore } from "../store/useProjectStore";

function getProjectStamp(state) {
  const refs = state?.selectActiveRefs?.();
  const project = refs?.project || null;
  const initiative = refs?.initiative || null;
  const projStamp = project?.meta?.updatedAt ?? project?.updatedAt ?? "";
  const initStamp = initiative?.updatedAt ?? "";
  return `${projStamp}::${initStamp}`;
}

export function useProjectPersistence({
  projectId,
  storage,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const versionRef = useRef(1);
  const lastSavedStampRef = useRef("");
  const isSaveInFlightRef = useRef(false);
  const saveQueuedRef = useRef(false);
  const isSavingRef = useRef(false);
  const suppressDirtyRef = useRef(false);
  const loadedProjectIdRef = useRef(null);
  const loadedModeRef = useRef(null);
  const loadProjectRef = useRef(storage?.loadProject);
  const saveProjectRef = useRef(storage?.saveProject);

  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  useEffect(() => {
    loadProjectRef.current = storage?.loadProject;
    saveProjectRef.current = storage?.saveProject;
  }, [storage?.loadProject, storage?.saveProject]);

  const saveNow = useCallback(async () => {
    if (!projectId || !saveProjectRef.current) return { ok: false };
    const snapshot = useProjectStore.getState().selectActiveRefs?.()?.project;
    if (!snapshot) return { ok: false };
    const currentStamp = getProjectStamp(useProjectStore.getState());
    if (!currentStamp || currentStamp === lastSavedStampRef.current) {
      setIsDirty(false);
      return { ok: true, skipped: true };
    }
    if (isSaveInFlightRef.current) {
      saveQueuedRef.current = true;
      return { ok: false, queued: true };
    }

    isSaveInFlightRef.current = true;
    setIsSaving(true);
    setSaveError(null);
    try {
      const result = await saveProjectRef.current(projectId, snapshot, {
        version: versionRef.current,
      });
      versionRef.current = result?.version || versionRef.current;
      lastSavedStampRef.current = getProjectStamp(useProjectStore.getState());
      setIsDirty(false);
      setLastSavedAt(Date.now());
      return { ok: true };
    } catch (err) {
      setSaveError(err);
      return { ok: false, error: err };
    } finally {
      isSaveInFlightRef.current = false;
      setIsSaving(false);
      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        void saveNow();
      }
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !loadProjectRef.current) return;
    if (loadedProjectIdRef.current === projectId) return;
    let isActive = true;
    suppressDirtyRef.current = true;
    setLoadError(null);
    const isFirstLoadForProject = loadedProjectIdRef.current !== projectId;
    const store = useProjectStore.getState();
    const cached = store.projects?.byId?.[projectId] || null;
    const allowCache = storage?.mode !== "cloud";
    const hasUsableCache = Boolean(cached && store.loadSnapshot && allowCache);
    const mode = storage?.mode || "local";
    const loadStartedStamp = getProjectStamp(useProjectStore.getState());
    if (isFirstLoadForProject && !hasUsableCache) setIsLoading(true);

    (async () => {
      try {
        if (hasUsableCache) {
          store.loadSnapshot(cached);
          lastSavedStampRef.current = getProjectStamp(useProjectStore.getState());
          setIsDirty(false);
        } else {
          const result = await loadProjectRef.current(projectId);
          if (result?.data && store.loadSnapshot) {
            const currentStamp = getProjectStamp(useProjectStore.getState());
            const hasLocalChangesSinceLoadStarted =
              Boolean(currentStamp) && currentStamp !== loadStartedStamp;
            if (!hasLocalChangesSinceLoadStarted) {
              store.loadSnapshot(result.data);
              lastSavedStampRef.current = getProjectStamp(useProjectStore.getState());
              setIsDirty(false);
            }
            versionRef.current = result?.version || versionRef.current || 1;
          } else if (
            storage?.mode !== "cloud" &&
            !store.projects?.allIds?.length &&
            store.initializeIfEmpty
          ) {
            store.initializeIfEmpty();
            lastSavedStampRef.current = getProjectStamp(useProjectStore.getState());
            setIsDirty(false);
          }
        }
        loadedProjectIdRef.current = projectId;
        loadedModeRef.current = mode;
      } catch (err) {
        if (isActive) setLoadError(err);
      } finally {
        if (isActive) suppressDirtyRef.current = false;
        if (isActive) setIsLoading(false);
      }
    })();

    return () => {
      isActive = false;
      suppressDirtyRef.current = false;
      setIsLoading(false);
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return () => {};

    const unsub = useProjectStore.subscribe(
      (state) => getProjectStamp(state),
      (nextStamp) => {
        if (suppressDirtyRef.current) return;
        if (nextStamp !== lastSavedStampRef.current) {
          setIsDirty(true);
          void saveNow();
        }
      },
      { equalityFn: Object.is }
    );

    return () => {
      unsub();
    };
  }, [projectId, saveNow]);

  useEffect(() => {
    if (!projectId) return () => {};
    if (typeof window === "undefined") return () => {};

    const flushOnHide = () => {
      if (document.visibilityState !== "hidden") return;
      const currentStamp = getProjectStamp(useProjectStore.getState());
      if (!currentStamp || currentStamp === lastSavedStampRef.current) return;
      if (isSavingRef.current) return;
      void saveNow();
    };

    document.addEventListener("visibilitychange", flushOnHide);
    window.addEventListener("pagehide", flushOnHide);
    return () => {
      document.removeEventListener("visibilitychange", flushOnHide);
      window.removeEventListener("pagehide", flushOnHide);
    };
  }, [projectId, saveNow]);

  return {
    isLoading,
    isSaving,
    isDirty,
    lastSavedAt,
    loadError,
    saveError,
    saveNow,
    clearErrors: () => {
      setLoadError(null);
      setSaveError(null);
    },
  };
}
